import { useCallback, useEffect, useRef, useState } from 'react';
import { clarityEvent, gaEvent } from '../analytics';
import { submitWaitlistJoin } from '../services/contactService';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
export const RECAPTCHA_ENABLED = Boolean(RECAPTCHA_SITE_KEY);

const isRecaptchaTimeout = (err) => {
  const msg = err?.message || err?.toString?.() || '';
  return typeof msg === 'string' && msg.toLowerCase().includes('timeout');
};

/**
 * Shared "join the waitlist" behaviour (validation, reCAPTCHA, submit, result popup).
 * Markup and styling stay with the consuming component.
 *
 * @param {object} options
 * @param {string} options.id            short id used in analytics reasons, e.g. 'hero'
 * @param {string} options.formName      analytics form name, e.g. 'hero_waitlist'
 * @param {() => void} [options.onPopupClose] extra work when the result popup is dismissed
 */
export default function useWaitlistSignup({ id, formName, onPopupClose }) {
  const [email, setEmail] = useState('');
  const [newsletterOptIn, setNewsletterOptIn] = useState(true);
  const [status, setStatus] = useState('idle');
  const [popup, setPopup] = useState({ isOpen: false, phase: 'loading', message: '' });
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [recaptchaError, setRecaptchaError] = useState('');
  const [showRecaptcha, setShowRecaptcha] = useState(false);

  const recaptchaRef = useRef(null);
  const popupButtonRef = useRef(null);
  const previousFocusRef = useRef(null);
  const onPopupCloseRef = useRef(onPopupClose);
  const hasTrackedFocusRef = useRef(false);
  const hasTrackedStartRef = useRef(false);

  useEffect(() => {
    onPopupCloseRef.current = onPopupClose;
  }, [onPopupClose]);

  const safeResetRecaptcha = useCallback((context) => {
    try {
      recaptchaRef.current?.reset?.();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.debug('Ignoring reCAPTCHA reset error', context, err);
      }
    }
    setRecaptchaToken('');
  }, []);

  const handleEmailChange = useCallback(
    (event) => {
      setEmail(event.target.value);
      setStatus((prev) => (prev === 'idle' ? prev : 'idle'));

      if (event.target.value && !hasTrackedStartRef.current) {
        hasTrackedStartRef.current = true;
        gaEvent('waitlist_email_started', { form_name: formName });
        clarityEvent('waitlist_email_started');
      }
    },
    [formName]
  );

  const handleEmailFocus = useCallback(() => {
    setShowRecaptcha(true);
    if (hasTrackedFocusRef.current) return;
    hasTrackedFocusRef.current = true;
    gaEvent('waitlist_email_focus', { form_name: formName });
    clarityEvent('waitlist_email_focus');
  }, [formName]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      gaEvent('waitlist_submit_attempt', { form_name: formName });
      clarityEvent('waitlist_submit_attempt');

      const normalizedEmail = email.trim().toLowerCase();
      setRecaptchaError('');

      if (!EMAIL_REGEX.test(normalizedEmail)) {
        setStatus('error');
        setPopup({
          isOpen: true,
          phase: 'error',
          message: 'Please enter a valid email address.',
        });
        return;
      }

      if (!RECAPTCHA_ENABLED) {
        setStatus('error');
        setRecaptchaError('reCAPTCHA is not configured.');
        gaEvent('recaptcha_error', { reason: `${id}_not_configured` });
        clarityEvent('recaptcha_error');
        return;
      }

      if (!recaptchaToken) {
        setShowRecaptcha(true);
        setStatus('error');
        setRecaptchaError('Please complete the reCAPTCHA.');
        gaEvent('recaptcha_error', { reason: `${id}_missing_token` });
        clarityEvent('recaptcha_error');
        return;
      }

      try {
        setStatus('submitting');
        setPopup({
          isOpen: true,
          phase: 'loading',
          message: 'Trying to join the waitlist...',
        });

        await submitWaitlistJoin({
          email: normalizedEmail,
          recaptchaToken,
          newsletterOptIn,
        });

        gaEvent('generate_lead', { page_section: formName, newsletter_opt_in: newsletterOptIn });
        clarityEvent(`${id}_waitlist_success`);

        setStatus('success');
        setPopup({
          isOpen: true,
          phase: 'success',
          message: newsletterOptIn
            ? 'We\u2019ll be in touch as we move closer to launch, and you\u2019ll get our newsletter too.'
            : 'We\u2019ll be in touch as we move closer to launch.',
        });
        setEmail('');
        setNewsletterOptIn(true);
        setRecaptchaError('');
        safeResetRecaptcha(`${id}-waitlist-success`);
      } catch (err) {
        setStatus('error');
        setPopup({
          isOpen: true,
          phase: 'error',
          message: err?.message || 'Could not join the waitlist right now. Please try again.',
        });
        gaEvent('form_error', { form_name: formName });
        clarityEvent(`${id}_waitlist_error`);
      }
    },
    [email, formName, id, newsletterOptIn, recaptchaToken, safeResetRecaptcha]
  );

  const closePopup = useCallback(() => {
    setPopup({ isOpen: false, phase: 'loading', message: '' });
    setStatus('idle');
    setRecaptchaError('');
    setShowRecaptcha(false);
    safeResetRecaptcha(`${id}-waitlist-popup-ok`);
    onPopupCloseRef.current?.();
  }, [id, safeResetRecaptcha]);

  const handleRecaptchaChange = useCallback((token) => {
    setRecaptchaToken(token || '');
    setRecaptchaError((prev) => (prev ? '' : prev));
  }, []);

  const handleRecaptchaExpired = useCallback(() => {
    setRecaptchaToken('');
    setRecaptchaError('reCAPTCHA expired. Please retry.');
    gaEvent('recaptcha_error', { reason: `${id}_expired` });
    clarityEvent('recaptcha_error');
  }, [id]);

  const handleRecaptchaErrored = useCallback(
    (err) => {
      setRecaptchaToken('');
      const timedOut = isRecaptchaTimeout(err);
      setRecaptchaError(
        timedOut ? 'reCAPTCHA timed out. Please retry.' : 'reCAPTCHA failed to load. Please retry.'
      );
      gaEvent('recaptcha_error', { reason: timedOut ? `${id}_timeout` : `${id}_load_failed` });
      clarityEvent('recaptcha_error');
    },
    [id]
  );

  // Focus trap + Escape key while the result popup is open.
  useEffect(() => {
    if (popup.isOpen) {
      previousFocusRef.current = document.activeElement;
      const timer = setTimeout(() => popupButtonRef.current?.focus(), 80);
      const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
          closePopup();
        }
        if (e.key === 'Tab') {
          e.preventDefault();
          popupButtonRef.current?.focus();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        clearTimeout(timer);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }

    if (previousFocusRef.current) {
      previousFocusRef.current?.focus?.();
      previousFocusRef.current = null;
    }
    return undefined;
  }, [popup.isOpen, closePopup]);

  return {
    email,
    setEmail,
    handleEmailChange,
    handleEmailFocus,
    newsletterOptIn,
    setNewsletterOptIn,
    // The newsletter opt-in only appears once the visitor has actually typed an email.
    showNewsletterOptIn: email.trim().length > 0,
    status,
    handleSubmit,
    popup,
    closePopup,
    popupButtonRef,
    recaptchaRef,
    recaptchaError,
    showRecaptcha,
    setShowRecaptcha,
    handleRecaptchaChange,
    handleRecaptchaExpired,
    handleRecaptchaErrored,
    recaptchaEnabled: RECAPTCHA_ENABLED,
    recaptchaSiteKey: RECAPTCHA_SITE_KEY,
  };
}
